import { useMemo, useState, useCallback, useEffect } from "react";
import { useQuery } from "@apollo/client";
import { useAuthUser } from "../../contexts/AuthUserContext";
import { useLocalization } from "../../contexts/LocalizationContext";
import { GET_ACTIONS_GROUPED_BY_FOCUS_AREA } from "../../graphql/queries";
import { API_BASE } from "../../utils/api/api";
import SelectedActionModal from "./SelectedActionModal";
import { ArrowLeftIcon, ArrowRightIcon, ListTodoIcon } from "lucide-react";
import styles from "./ZeqiKeyFocusAreaSection.module.css";

const LEADING_BRACKET_TAG_REGEX = /^\s*\[[^\]]+\]\s*/;
const formatTaskTitle = (value, fallback = "Task") => {
  const clean = String(value || "").replace(LEADING_BRACKET_TAG_REGEX, "").trim();
  return clean || fallback;
};

const normSearch = (value) => String(value || "").toLowerCase().trim();

const STATUS = {
  suggested: "Suggested",
  running: "Running",
};

const TYPE_CONFIG = {
  PRICING: { label: "Pricing", color: "#90CAF9" },
  ADVERTISING: { label: "Advertising", color: "#EFA4C9" },
  CRM_EMAIL: { label: "CRM & Email", color: "#FFAB91" },
  ORGANIC_SOCIAL: { label: "Organic Social", color: "#FFD6A5" },
  WEB_CRO: { label: "Web CRO", color: "#4DB6AC" },
  SEO: { label: "SEO", color: "#A5D6A7" },
  PRODUCT_INTEL: { label: "Product Intel", color: "#CE93D8" },
  INFLUENCER: { label: "Influencer", color: "#F8BBD0" },
  ONBOARDING: { label: "Onboarding", color: "#64B5F6" },
  FOUNDATION_STAGE: { label: "Foundation Stage", color: "#9575CD" },
};

// OAuth constants
const META_APP_ID = import.meta.env.VITE_META_APP_ID;
const META_REDIRECT_URI = `${API_BASE}/facebook/callback`;
const META_SCOPES = [
  "email",
  "read_insights",
  "business_management",
  "public_profile",
  "ads_read",
  "ads_management",
  "pages_show_list",
  "pages_read_engagement",
  "pages_manage_posts",
  "pages_manage_metadata",
  "instagram_basic",
  "instagram_content_publish",
].join(",");

const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID;
const GOOGLE_REDIRECT_URI = `${API_BASE}/google/callback`;
const GOOGLE_SCOPES = [
  "openid",
  "https://www.googleapis.com/auth/userinfo.email",
  "https://www.googleapis.com/auth/userinfo.profile",
  "https://www.googleapis.com/auth/analytics.readonly",
  "https://www.googleapis.com/auth/webmasters.readonly",
  "https://www.googleapis.com/auth/webmasters",
  "https://www.googleapis.com/auth/adsense.readonly",
  "https://www.googleapis.com/auth/content",
  "https://www.googleapis.com/auth/siteverification",
].join(" ");

const ZeqiKeyFocusAreaSection = ({
  userId,
  isGoogleConnected = false,
  isFacebookConnected = false,
  googleSyncReady = false,
  facebookSyncReady = false,
  tokensLoading = false,
  shopDataReadiness = null,
  shopDataReadinessLoading = false,
  refetchTokens,
  onOpenProductCosts,
  onOpenOperatingCosts,
}) => {
  const { user } = useAuthUser();
  const { t, locale } = useLocalization();
  const isRTL = locale === "ar";
  const authToken = user?.token;
  const resolvedUserId = user?._id || user?.id || user?.userId;

  const [localGoogleConnected, setLocalGoogleConnected] =
    useState(isGoogleConnected);
  const [localFacebookConnected, setLocalFacebookConnected] =
    useState(isFacebookConnected);

  useEffect(() => {
    setLocalGoogleConnected(isGoogleConnected);
  }, [isGoogleConnected]);

  useEffect(() => {
    setLocalFacebookConnected(isFacebookConnected);
  }, [isFacebookConnected]);

  const waitingOnGoogleSync = localGoogleConnected && !googleSyncReady;
  const waitingOnFacebookSync = localFacebookConnected && !facebookSyncReady;
  const productCostReady = shopDataReadiness?.missing?.product_cost === false;
  const operatingCostReady =
    shopDataReadiness?.missing?.operating_cost === false;
  const waitingOnShopData = !(productCostReady && operatingCostReady);
  const needsConnections =
    !localGoogleConnected ||
    !localFacebookConnected ||
    waitingOnGoogleSync ||
    waitingOnFacebookSync ||
    waitingOnShopData;

  const safeRefetchTokens = useCallback(() => {
    if (typeof refetchTokens === "function") return refetchTokens();
    return Promise.resolve();
  }, [refetchTokens]);

  const startFacebookOAuth = useCallback(() => {
    if (!resolvedUserId) return;
    setLocalFacebookConnected(true);
    const state = encodeURIComponent(resolvedUserId);
    const authUrl =
      `https://www.facebook.com/v23.0/dialog/oauth?client_id=${META_APP_ID}` +
      `&redirect_uri=${encodeURIComponent(META_REDIRECT_URI)}` +
      `&response_type=code&scope=${encodeURIComponent(META_SCOPES)}` +
      `&state=${state}`;
    window.open(authUrl, "_blank", "noopener");
    // Poll for connection after OAuth window opens
    setTimeout(() => safeRefetchTokens().catch(() => {}), 3000);
  }, [resolvedUserId, safeRefetchTokens]);

  const startGoogleOAuth = useCallback(() => {
    if (!resolvedUserId) return;
    setLocalGoogleConnected(true);
    const state = encodeURIComponent(resolvedUserId);
    const authUrl =
      `https://accounts.google.com/o/oauth2/v2/auth?client_id=${GOOGLE_CLIENT_ID}` +
      `&redirect_uri=${encodeURIComponent(GOOGLE_REDIRECT_URI)}` +
      `&response_type=code&scope=${encodeURIComponent(GOOGLE_SCOPES)}` +
      `&access_type=offline&prompt=consent&state=${state}`;
    window.open(authUrl, "_blank", "noopener");
    // Poll for connection after OAuth window opens
    setTimeout(() => safeRefetchTokens().catch(() => {}), 3000);
  }, [resolvedUserId, safeRefetchTokens]);

  const [activeTab, setActiveTab] = useState(
    t("ZeqiKeyFocusArea.tabs.suggested"),
  );
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedRow, setSelectedRow] = useState(null);
  const [expandActionId, setExpandActionId] = useState(null);
  const safeT = useCallback(
    (key, fallback) => {
      const value = t(key);
      if (
        typeof value === "string" &&
        value.trim() &&
        !value.startsWith('[missing "')
      ) {
        return value;
      }
      return fallback;
    },
    [t],
  );

  useEffect(() => {
    setActiveTab(t("ZeqiKeyFocusArea.tabs.suggested"));
  }, [locale, t]);

  useEffect(() => {
    setSearchQuery("");
  }, [activeTab]);

  const { data, loading, error, refetch, networkStatus } = useQuery(
    GET_ACTIONS_GROUPED_BY_FOCUS_AREA,
    {
      variables: userId ? { userId } : undefined,
      skip: needsConnections || !authToken,
      notifyOnNetworkStatusChange: true,
      fetchPolicy: "cache-and-network",
      context: {
        headers: authToken ? { Authorization: `Bearer ${authToken}` } : {},
      },
    },
  );

  const rows = useMemo(() => {
    const groups = data?.getActionsGroupedByFocusArea?.focusAreas ?? [];

    const resolveOrder = (raw) => {
      if (raw === null || raw === undefined || raw === "") return Infinity;
      const num = Number(raw);
      return Number.isFinite(num) ? num : Infinity;
    };

    const isRunningTask = (taskNode) =>
      String(taskNode?.status || "").toLowerCase() === "running";
    const isCompletedTask = (taskNode) => taskNode?.isCompleted === true;
    const isPendingTask = (taskNode) =>
      !isCompletedTask(taskNode) && !isRunningTask(taskNode);

    const isSuggestedTab = activeTab === t("ZeqiKeyFocusArea.tabs.suggested");
    const filterTasksForTab = (tasks = []) => {
      if (isSuggestedTab) {
        return tasks.filter((taskNode) => isPendingTask(taskNode));
      }
      return tasks.filter(
        (taskNode) => isRunningTask(taskNode) || isCompletedTask(taskNode),
      );
    };

    const shouldIncludeAction = (action) => {
      const tasks = Array.isArray(action?.tasks) ? action.tasks : [];
      const runningOrCompleted = tasks.some(
        (taskNode) => isRunningTask(taskNode) || isCompletedTask(taskNode),
      );
      if (isSuggestedTab) {
        const hasPending = tasks.some((taskNode) => isPendingTask(taskNode));
        return hasPending;
      }
      return runningOrCompleted;
    };

    return groups
      .map((group) => {
        const actions = (group?.actions || [])
          .filter((action) => shouldIncludeAction(action))
          .map((action) => ({
            ...action,
            tasks: filterTasksForTab(action?.tasks || []),
          }))
          .filter((action) => action.tasks?.length > 0);
        if (!actions.length) return null;

        const typeKey = actions[0]?.department || actions[0]?.focusArea;
        const cfg = TYPE_CONFIG[typeKey] || {};
        const order = Math.min(
          ...actions.map((action) => resolveOrder(action?.order)),
        );

        // Count AI and User tasks
        let aiTaskCount = 0;
        let userTaskCount = 0;
        actions.forEach((action) => {
          action.tasks?.forEach((task) => {
            const assignedTo = String(task?.assignedTo || "").toLowerCase();
            if (assignedTo === "ai") {
              aiTaskCount++;
            } else if (assignedTo === "user") {
              userTaskCount++;
            }
          });
        });

        return {
          key: `${group?.focusArea}-${isSuggestedTab ? "suggested" : "running"}`,
          focusArea: group?.focusArea || t("SuggestedScreen.focusAreaFallback"),
          pillLabel: cfg.label,
          pillColor: cfg.color || "#EEE",
          actions,
          order,
          aiTaskCount,
          userTaskCount,
        };
      })
      .filter(Boolean)
      .sort((a, b) => a.order - b.order);
  }, [data, activeTab, t]);

  const isRefreshing =
    networkStatus === 4 ||
    (needsConnections &&
      (tokensLoading ||
        shopDataReadinessLoading ||
        waitingOnGoogleSync ||
        waitingOnFacebookSync));
  const isSuggestedTab = activeTab === t("ZeqiKeyFocusArea.tabs.suggested");

  const getTaskStatus = useCallback(
    (taskNode) => {
      const rawStatus = String(taskNode?.status || "").toLowerCase();
      if (taskNode?.isCompleted) {
        return {
          label: safeT("SelectedSuggestedActionSection.completed", "Complete"),
          className: styles.statusComplete,
        };
      }
      if (rawStatus === "running") {
        return {
          label: safeT("SelectedSuggestedActionSection.running", "In Progress"),
          className: styles.statusInProgress,
        };
      }
      return {
        label: taskNode?.status || "Pending",
        className: styles.statusPending,
      };
    },
    [safeT],
  );

  const runningTasks = useMemo(
    () =>
      rows.flatMap((row) =>
        (row?.actions || []).flatMap((action) =>
          (action?.tasks || []).map((taskNode, index) => ({
            key:
              taskNode?._id ||
              taskNode?.id ||
              `${row.key}-${action?._id || action?.id || "action"}-${index}`,
            actionId:
              taskNode?.action_id ||
              action?.id ||
              action?._id ||
              "unknown-action",
            title:
              formatTaskTitle(
                taskNode?.task_title ||
                taskNode?.title ||
                taskNode?.task,
                action?.title || row?.focusArea || "Task",
              ),
            actionTitle: action?.title || row?.focusArea,
            focusArea:
              taskNode?.focusArea || action?.focusArea || row?.focusArea || "-",
            status: getTaskStatus(taskNode),
          })),
        ),
      ),
    [rows, getTaskStatus],
  );

  const runningTaskGroups = useMemo(() => {
    const grouped = new Map();
    runningTasks.forEach((task) => {
      const id = task.actionId || "unknown-action";
      if (!grouped.has(id)) {
        grouped.set(id, {
          actionId: id,
          focusArea: task.focusArea,
          actionTitle: task.actionTitle,
          tasks: [],
        });
      }
      grouped.get(id).tasks.push(task);
    });
    return Array.from(grouped.values());
  }, [runningTasks]);

  const filteredRows = useMemo(() => {
    const q = normSearch(searchQuery);
    if (!q) return rows;

    return rows
      .map((row) => {
        const topLevelMatch = [row?.focusArea, row?.pillLabel].some((value) =>
          normSearch(value).includes(q),
        );

        const visibleActions = topLevelMatch
          ? row.actions || []
          : (row.actions || [])
              .map((action) => {
                const actionMatch = [
                  action?.title,
                  action?.group_title,
                  action?.department,
                  action?.focusArea,
                  action?.product_name,
                  action?.article_title,
                  action?.blog_title,
                  action?.description,
                ].some((value) => normSearch(value).includes(q));

                if (actionMatch) {
                  return action;
                }

                const visibleTasks = (action?.tasks || []).filter((task) =>
                  [
                    task?.task_title,
                    task?.title,
                    task?.name,
                    task?.task,
                    task?.description,
                    task?.task_description,
                    task?.product_name,
                    task?.article_title,
                    task?.blog_title,
                  ].some((value) => normSearch(value).includes(q)),
                );

                if (!visibleTasks.length) return null;
                return { ...action, tasks: visibleTasks };
              })
              .filter(Boolean);

        if (!visibleActions.length) return null;

        let aiTaskCount = 0;
        let userTaskCount = 0;
        visibleActions.forEach((action) => {
          (action?.tasks || []).forEach((task) => {
            const assignedTo = String(task?.assignedTo || "").toLowerCase();
            if (assignedTo === "ai") aiTaskCount += 1;
            else if (assignedTo === "user") userTaskCount += 1;
          });
        });

        return {
          ...row,
          actions: visibleActions,
          aiTaskCount,
          userTaskCount,
        };
      })
      .filter(Boolean);
  }, [rows, searchQuery]);

  const filteredRunningTaskGroups = useMemo(() => {
    const q = normSearch(searchQuery);
    if (!q) return runningTaskGroups;

    return runningTaskGroups
      .map((group) => {
        const groupMatch = [group?.focusArea, group?.actionTitle].some((value) =>
          normSearch(value).includes(q),
        );
        const tasks = groupMatch
          ? group.tasks
          : (group.tasks || []).filter((task) =>
              [task?.title, task?.actionTitle, task?.focusArea].some((value) =>
                normSearch(value).includes(q),
              ),
            );

        if (!tasks.length) return null;
        return { ...group, tasks };
      })
      .filter(Boolean);
  }, [runningTaskGroups, searchQuery]);

  if (needsConnections) {
    return (
      <div className={styles.container}>
        {/* <h3 className={`${styles.header} ${isRTL ? styles.rtl : ""}`}>
          {t("ZeqiKeyFocusArea.header")}
        </h3> */}
        <div className={styles.connectCard}>
          <h4 className={`${styles.connectTitle} ${isRTL ? styles.rtl : ""}`}>
            {t("StrategyReportSection.connect.title")}
          </h4>
          <p className={`${styles.connectBody} ${isRTL ? styles.rtl : ""}`}>
            {t("StrategyReportSection.connect.why")}
          </p>
          <p
            className={`${styles.connectBody} ${isRTL ? styles.rtl : ""}`}
            style={{ marginTop: "0.5rem" }}
          >
            {t("StrategyReportSection.connect.body")}
          </p>
          {(waitingOnGoogleSync || waitingOnFacebookSync) && (
            <p
              className={`${styles.connectBody} ${isRTL ? styles.rtl : ""}`}
              style={{ marginTop: "0.75rem" }}
            >
              {t("StrategyReportSection.connect.syncing")}
            </p>
          )}
          {(tokensLoading ||
            shopDataReadinessLoading ||
            waitingOnGoogleSync ||
            waitingOnFacebookSync) && (
            <div className={styles.statusRow}>
              <div className={styles.spinner} />
              <span>{t("StrategyReportSection.connect.checking")}</span>
            </div>
          )}

          <div className={styles.connectButtonsRow}>
            <button
              type="button"
              className={`${styles.connectBtn} ${styles.meta}`}
              onClick={startFacebookOAuth}
            >
              <span
                className={`${styles.icon} ${styles.metaIcon}`}
                aria-hidden="true"
              >
                <svg viewBox="0 0 48 48" focusable="false">
                  <circle cx="24" cy="24" r="24" fill="#1877F2" />
                  <path
                    fill="#FFFFFF"
                    d="M27.4 38V24.7h4.5l.7-5.2h-5.2v-3.3c0-1.5.4-2.6 2.6-2.6h2.8V9c-.5-.1-2.2-.2-4.2-.2-4.2 0-7 2.6-7 7.3v3.4h-4.7v5.2h4.7V38h4.8z"
                  />
                </svg>
              </span>
              {localFacebookConnected
                ? t("StrategyReportSection.connect.metaConnected")
                : t("StrategyReportSection.connect.metaCta")}
            </button>
            <button
              type="button"
              className={`${styles.connectBtn} ${styles.google}`}
              onClick={startGoogleOAuth}
            >
              <span
                className={`${styles.icon} ${styles.googleIcon}`}
                aria-hidden="true"
              >
                <svg viewBox="0 0 48 48" focusable="false">
                  <path
                    fill="#EA4335"
                    d="M24 9.5c3.54 0 6.72 1.22 9.22 3.61l6.9-6.9C35.9 2.38 30.47 0 24 0 14.62 0 6.44 5.38 2.56 13.22l8.06 6.26C12.5 13.5 17.74 9.5 24 9.5z"
                  />
                  <path
                    fill="#4285F4"
                    d="M46.98 24.55c0-1.64-.15-3.22-.43-4.74H24v9.02h12.94c-.56 3.01-2.24 5.56-4.78 7.27l7.73 5.98c4.51-4.17 7.09-10.32 7.09-17.53z"
                  />
                  <path
                    fill="#FBBC05"
                    d="M10.62 28.48c-.53-1.58-.83-3.27-.83-5s.3-3.42.83-5l-8.06-6.26C.92 15.46 0 19.64 0 24s.92 8.54 2.56 12.78l8.06-6.26z"
                  />
                  <path
                    fill="#34A853"
                    d="M24 48c6.48 0 11.93-2.13 15.9-5.81l-7.73-5.98c-2.15 1.45-4.92 2.3-8.17 2.3-6.26 0-11.5-4-13.38-9.5l-8.06 6.26C6.44 42.62 14.62 48 24 48z"
                  />
                </svg>
              </span>
              {localGoogleConnected
                ? t("StrategyReportSection.connect.googleConnected")
                : t("StrategyReportSection.connect.googleCta")}
            </button>
          </div>
          <div className={styles.connectButtonsRow}>
            <button
              type="button"
              className={`${styles.connectBtn} ${styles.costBtn} ${
                productCostReady ? styles.costBtnComplete : ""
              }`}
              onClick={() => onOpenProductCosts?.()}
            >
              {productCostReady
                ? t("StrategyReportSection.connect.productCostConnected")
                : t("StrategyReportSection.connect.productCostCta")}
            </button>
            <button
              type="button"
              className={`${styles.connectBtn} ${styles.costBtn} ${
                operatingCostReady ? styles.costBtnComplete : ""
              }`}
              onClick={() => onOpenOperatingCosts?.()}
            >
              {operatingCostReady
                ? t("StrategyReportSection.connect.operatingCostConnected")
                : t("StrategyReportSection.connect.operatingCostCta")}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (loading && !data) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingOnly} aria-label="Loading" role="status">
          <span className={styles.loadingSpinner} />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.container}>
        <p className={styles.error}>
          {t("ZeqiKeyFocusArea.loadFailPrefix")} {error.message}
        </p>
        <button type="button" onClick={() => refetch?.()}>
          {t("ZeqiKeyFocusArea.tryAgain")}
        </button>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.tabsRow}>
        <div className={styles.tabs}>
          {[
            t("ZeqiKeyFocusArea.tabs.suggested"),
            t("ZeqiKeyFocusArea.tabs.running"),
          ].map((tab) => (
            <button
              key={tab}
              type="button"
              className={`${styles.tab} ${tab === activeTab ? styles.tabActive : ""}`}
              onClick={() => setActiveTab(tab)}
            >
              {tab}
            </button>
          ))}
        </div>
        <div className={styles.tabsSearchWrap}>
          <input
            type="search"
            className={styles.tabsSearchInput}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search product, blog, action, or task..."
            aria-label="Search execution actions and tasks"
          />
          {searchQuery ? (
            <button
              type="button"
              className={styles.tabsSearchClear}
              onClick={() => setSearchQuery("")}
              aria-label="Clear search"
            >
              Clear
            </button>
          ) : null}
        </div>
      </div>

      {isRefreshing && <div className={styles.loading}>Refreshing...</div>}

      {isSuggestedTab ? (
        <ul className={styles.reportGrid}>
          {filteredRows.map((row) => {
            const pressable = true; // or set based on your conditions
            const chevron = isRTL ? (
              <ArrowLeftIcon size={18} />
            ) : (
              <ArrowRightIcon size={18} />
            );

            return (
              <li key={row.key} className={styles.reportItem}>
                <button
                  type="button"
                  className={`${styles.reportTile} ${!pressable ? styles.reportTileDisabled : ""}`}
                  onClick={() => pressable && setSelectedRow(row)}
                  disabled={!pressable}
                >
                  {/* Badge with total task count */}
                  {row.aiTaskCount + row.userTaskCount > 0 && (
                    <div className={styles.badgeContainer}>
                      <ListTodoIcon className={styles.taskIcon} size={22} />
                      <span className={styles.badge}>
                        {row.aiTaskCount + row.userTaskCount}
                      </span>
                      <div className={styles.tooltip}>
                        {row.aiTaskCount > 0 && (
                          <div>
                            <strong>AI Tasks:</strong> {row.aiTaskCount}
                          </div>
                        )}
                        {row.userTaskCount > 0 && (
                          <div>
                            <strong>User Tasks:</strong> {row.userTaskCount}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  <div className={styles.reportContent}>
                    <div className={styles.actionMeta}>
                      {row.pillLabel && (
                        <span
                          className={styles.pill}
                          style={{ backgroundColor: row.pillColor }}
                        >
                          {row.pillLabel}
                        </span>
                      )}
                    </div>
                    <span
                      className={`${styles.reportTitle} ${isRTL ? styles.rtl : ""}`}
                    >
                      {row.focusArea}
                    </span>
                    {pressable ? (
                      <div className={styles.reportActions}>
                        <div className={styles.actionRight}>
                          <span
                            className={styles.actionButton}
                            aria-hidden="true"
                          >
                            <span className={styles.chevron}>{chevron}</span>
                          </span>
                          <span className={styles.explorePill}>
                            {t("ReportsPage.explore") || "Explore"}
                          </span>
                        </div>
                      </div>
                    ) : (
                      <span className={styles.reportHint}>
                        {t("ReportsPage.generating") ||
                          "Generating. You will be notified."}
                      </span>
                    )}
                  </div>
                </button>
              </li>
            );
          })}

          {!filteredRows.length && (
            <li className={styles.reportItem}>
              <p className={`${styles.subtle} ${isRTL ? styles.rtl : ""}`}>
                {searchQuery
                  ? "No matching products, blogs, actions, or tasks."
                  : t("ZeqiKeyFocusArea.emptySuggested")}
              </p>
            </li>
          )}
        </ul>
      ) : (
        <div className={styles.runningListWrap}>
          <div className={styles.runningListHeader}>
            <span>Task</span>
            <span>Status</span>
          </div>

          <ul className={styles.runningList}>
            {filteredRunningTaskGroups.map((group) => {
              return (
                <li key={group.actionId} className={styles.runningGroupItem}>
                  <div className={styles.runningGroupHeader}>
                    {group.focusArea}
                  </div>
                  <ul className={styles.runningGroupTasks}>
                    {group.tasks.map((task) => (
                      <li key={task.key} className={styles.runningListItem}>
                        <div className={styles.runningRow}>
                          <span className={styles.runningTitle}>
                            {formatTaskTitle(task.title)}
                          </span>
                          <span
                            className={`${styles.runningStatus} ${task.status.className}`}
                          >
                            <span
                              className={styles.statusDot}
                              aria-hidden="true"
                            />
                            {task.status.label}
                          </span>
                        </div>
                      </li>
                    ))}
                  </ul>
                </li>
              );
            })}

            {!filteredRunningTaskGroups.length && (
              <li className={styles.runningListItem}>
                <p className={`${styles.subtle} ${isRTL ? styles.rtl : ""}`}>
                  {searchQuery
                    ? "No matching products, blogs, actions, or tasks."
                    : t("ZeqiKeyFocusArea.emptyRunning")}
                </p>
              </li>
            )}
          </ul>
        </div>
      )}

      <SelectedActionModal
        isOpen={Boolean(selectedRow)}
        row={selectedRow}
        onClose={() => {
          setSelectedRow(null);
          setExpandActionId(null);
        }}
        activeTab={activeTab}
        onUpdateRow={(updatedRow) => setSelectedRow(updatedRow)}
        onActionCompleted={() => refetch?.()}
        expandActionId={expandActionId}
        onRunTask={(actionId) => setExpandActionId(actionId)}
        onRunAllStarted={() => {
          setActiveTab(t("ZeqiKeyFocusArea.tabs.running"));
          setSelectedRow(null);
          setExpandActionId(null);
        }}
        initialSearchQuery={searchQuery}
      />
    </div>
  );
};

export default ZeqiKeyFocusAreaSection;
